import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText, tertiaryText } from '../../../../styling/stylingHelper';
import type { ChatTurn } from '../../../../stores/appCloudAgentStore';
import { CloudMark } from './CloudMark';
import { currentActivityLine, elapsedSeconds, humanisedSteps } from './statusText';

/** How often the running timer re-renders. Whole seconds are displayed, so 1s is enough. */
const TICK_MS = 1000;

/** The expandable step trail — the check/dot animation the full-page loader used to own. */
const StepList: React.FC<{ steps: string[]; activeIndex: number }> = ({ steps, activeIndex }) => (
  <motion.div
    className="flex flex-col gap-2 pl-1 mt-2"
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: 'auto' }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ duration: 0.2 }}
  >
    {steps.map((step, i) => {
      const isDone = i < activeIndex;
      const isActive = i === activeIndex;
      return (
        <motion.div
          key={`${i}-${step}`}
          className="flex items-center gap-3"
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: isDone || isActive ? 1 : 0.35, x: 0 }}
          transition={{ duration: 0.25 }}
        >
          <span className="flex items-center justify-center w-5 h-5 shrink-0">
            {isDone ? (
              <Check className="w-4 h-4 text-green-500 dark:text-textGreen-dark" />
            ) : isActive ? (
              <motion.span
                className="w-2.5 h-2.5 rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark"
                animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
                transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
              />
            ) : (
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600" />
            )}
          </span>
          <span className={cn('text-sm', isActive ? primaryText : secondaryText)}>{step}</span>
        </motion.div>
      );
    })}
  </motion.div>
);

/**
 * The agent's "what am I doing" indicator: ONE line by default — brand mark, the current step in
 * plain language, and a chevron — expanding on click to the full step trail.
 *
 * It replaces the old full-height staged loader, which took most of a phone screen for every
 * answer. Expanded state is component-local on purpose: it lives and dies with this turn, so
 * expanding one turn never expands the rest of the transcript.
 */
export const TurnActivity: React.FC<{
  turn: Pick<ChatTurn, 'status' | 'startedAt' | 'endedAt'>;
  /** Working (pulsing mark, live timer) vs finished (muted summary). */
  running: boolean;
}> = ({ turn, running }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Only tick while running; a finished turn's duration is fixed.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [running]);

  const steps = humanisedSteps(turn.status);
  const seconds = elapsedSeconds(turn.startedAt, turn.endedAt, now);

  // While running the newest step is the active one; once finished every step is done.
  const activeIndex = running ? Math.max(0, steps.length - 1) : steps.length;

  const line = running
    ? currentActivityLine(turn.status) || t('cloudAgent.activity.thinking')
    : t('cloudAgent.activity.summary', { count: steps.length, seconds });

  const canExpand = steps.length > 0;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => canExpand && setExpanded((v) => !v)}
        aria-expanded={expanded}
        disabled={!canExpand}
        className={cn(
          'flex items-center gap-2 text-left rounded-lg -mx-1 px-1 py-0.5 transition',
          canExpand && 'hover:bg-purple-50 dark:hover:bg-purple-900/20',
          !canExpand && 'cursor-default'
        )}
      >
        <CloudMark size={20} pulsing={running} />
        <span className={cn('text-sm truncate', running ? secondaryText : tertiaryText)}>
          {line}
        </span>
        {canExpand && (
          <ChevronDown
            className={cn(
              'w-3.5 h-3.5 shrink-0 transition-transform',
              tertiaryText,
              expanded && 'rotate-180'
            )}
          />
        )}
      </button>

      <AnimatePresence initial={false}>
        {expanded && canExpand && <StepList steps={steps} activeIndex={activeIndex} />}
      </AnimatePresence>
    </div>
  );
};

export default TurnActivity;
